import type { GeneratedFile } from "./types";

export type TemplateId =
  | "base-nextjs"
  | "tpl-landing"
  | "tpl-dashboard"
  | "tpl-portfolio"
  | "tpl-blog"
  | "tpl-store"
  | "tpl-crm"
  | "tpl-booking"
  | "tpl-project"
  | "tpl-ai"
  | "tpl-social"
  | "tpl-marketplace";

export interface TemplateInfo {
  id: TemplateId;
  name: string;
  description: string;
  snapshotUrl: string;
  /** Single-file template source. Mounted as app/page.tsx on top of the
   *  base snapshot so the AI can patch a real working starting point
   *  instead of generating from scratch. */
  initialFiles?: GeneratedFile[];
}

export interface TemplateOption extends TemplateInfo {
  tagline: string;
  label: string;
}

export interface TemplateStarterExample {
  templateId: TemplateId;
  prompt: string;
  label: string;
}

/**
 * Lean templates — each has a main page.tsx plus 2-3 small extra route
 * pages (~40-80 lines each) and a TEMPLATE.md AI instruction guide. They
 * mount on the base-nextjs snapshot. The AI sees ALL files via the
 * /api/template-files endpoint and can patch them efficiently.
 *
 * Each template directory contains:
 *   TEMPLATE.md          — AI: read-before-editing guide (rules, file map, safe areas)
 *   app/page.tsx          — main page (<200 lines, full JSDoc at top)
 *   app/<sub-route>/page.tsx — 2-3 lean secondary pages
 *
 * Old v0-* templates had 50-300 files / 100MB+ which made follow-up edits
 * break because the AI couldn't hold the whole app in context.
 */
const TEMPLATES: Record<TemplateId, TemplateInfo> = {
  "base-nextjs": {
    id: "base-nextjs",
    name: "Blank",
    description: "A clean Next.js starter. Start from scratch.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-landing": {
    id: "tpl-landing",
    name: "SaaS Landing Page",
    description: "Hero, pricing cards, FAQ, and footer.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-dashboard": {
    id: "tpl-dashboard",
    name: "Analytics Dashboard",
    description: "KPI cards, chart, sidebar, and orders table.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-portfolio": {
    id: "tpl-portfolio",
    name: "Creative Portfolio",
    description: "Project grid, about section, and contact.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-blog": {
    id: "tpl-blog",
    name: "Content Blog",
    description: "Article grid with search and tag filters.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-store": {
    id: "tpl-store",
    name: "E-Commerce Store",
    description: "Product grid with cart and category filters.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-crm": {
    id: "tpl-crm",
    name: "CRM Dashboard",
    description: "Sales pipeline, contacts, deals, and activity feed.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-booking": {
    id: "tpl-booking",
    name: "Booking Platform",
    description: "Service catalog, calendar scheduling, and reviews.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-project": {
    id: "tpl-project",
    name: "Project Manager",
    description: "Kanban board, task cards, and team collaboration.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-ai": {
    id: "tpl-ai",
    name: "AI SaaS Platform",
    description: "API showcase, usage pricing, and code examples.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-social": {
    id: "tpl-social",
    name: "Community Forum",
    description: "Discussion threads, member profiles, and trending tags.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
  "tpl-marketplace": {
    id: "tpl-marketplace",
    name: "Job Board",
    description: "Job listings, company profiles, and search filters.",
    snapshotUrl: "/snapshots/base-nextjs.bin.gz",
  },
};

export const TEMPLATE_OPTIONS: TemplateOption[] = Object.values(TEMPLATES).map(
  (t) => ({
    ...t,
    tagline: t.description,
    label: t.name,
  }),
);

export const TEMPLATE_STARTER_EXAMPLES: TemplateStarterExample[] = [
  {
    templateId: "base-nextjs",
    prompt: "A personal portfolio with projects, writing, and contact.",
    label: "Blank",
  },
  {
    templateId: "tpl-landing",
    prompt: "A SaaS landing page for a project management tool with pricing and FAQ.",
    label: "SaaS Landing Page",
  },
  {
    templateId: "tpl-dashboard",
    prompt: "An analytics dashboard showing revenue, users, and recent orders.",
    label: "Analytics Dashboard",
  },
  {
    templateId: "tpl-portfolio",
    prompt: "A design portfolio showcasing branding and web projects.",
    label: "Creative Portfolio",
  },
  {
    templateId: "tpl-blog",
    prompt: "A tech blog with articles about web development and design.",
    label: "Content Blog",
  },
  {
    templateId: "tpl-store",
    prompt: "An online store selling handcrafted home goods.",
    label: "E-Commerce Store",
  },
  {
    templateId: "tpl-crm",
    prompt: "A CRM dashboard for managing sales pipeline, contacts, and deals.",
    label: "CRM Dashboard",
  },
  {
    templateId: "tpl-booking",
    prompt: "A booking platform for a salon with services, scheduling, and reviews.",
    label: "Booking Platform",
  },
  {
    templateId: "tpl-project",
    prompt: "A project management tool with a kanban board and team collaboration.",
    label: "Project Manager",
  },
  {
    templateId: "tpl-ai",
    prompt: "An AI SaaS platform landing page with API docs, pricing, and code examples.",
    label: "AI SaaS Platform",
  },
  {
    templateId: "tpl-social",
    prompt: "A developer community forum with discussions, tags, and member profiles.",
    label: "Community Forum",
  },
  {
    templateId: "tpl-marketplace",
    prompt: "A tech job board with listings, company profiles, and salary filters.",
    label: "Job Board",
  },
];

export function getTemplate(id: string): TemplateInfo | null {
  return (TEMPLATES as Record<string, TemplateInfo | undefined>)[id] ?? null;
}

export function listTemplates(): TemplateInfo[] {
  return Object.values(TEMPLATES);
}

export function isTemplateId(id: string | undefined | null): id is TemplateId {
  if (!id) return false;
  return id in TEMPLATES;
}

/**
 * Slugs the public templates gallery must never list.
 *
 * `base-nextjs` ("Blank") is a real build starting point — the empty Next.js
 * starter every from-scratch build mounts — and stays fully available to the
 * build flow via {@link TEMPLATES} and /api/template-files. It is NOT a
 * ready-to-clone showcase, though, so browsing surfaces (the /templates gallery
 * and the homepage "Start from a Proven Business" carousel) exclude it to avoid
 * offering an empty starter alongside real prebuilt templates.
 *
 * Matched by exact slug — never a fuzzy name match — so a real template that
 * merely happens to contain "blank" in its copy is unaffected.
 */
export const GALLERY_EXCLUDED_TEMPLATE_SLUGS: readonly string[] = ["base-nextjs"];

/** True when a template with this slug may appear in the public gallery. */
export function isGalleryTemplateSlug(slug: string | null | undefined): boolean {
  return !!slug && !GALLERY_EXCLUDED_TEMPLATE_SLUGS.includes(slug);
}

/**
 * Returns true when the template already satisfies the user's prompt,
 * meaning we can skip the initial AI patch and just render the template
 * stock. Currently matches on an exact starter-example prompt for the
 * given template.
 */
export function shouldSkipInitialTemplatePatch(
  id: string | undefined | null,
  prompt?: string,
): boolean {
  if (!id) return false;
  if (!prompt) return false;
  const ex = TEMPLATE_STARTER_EXAMPLES.find((e) => e.templateId === id);
  if (!ex) return false;
  return ex.prompt.trim() === prompt.trim();
}

/**
 * Best-effort template selection based on keywords in the user's prompt.
 */
export function selectTemplateForPrompt(prompt: string): TemplateId {
  const p = prompt.toLowerCase();
  const rules: { id: TemplateId; keywords: string[] }[] = [
    { id: "tpl-crm", keywords: ["crm", "sales pipeline", "contacts", "deals", "lead management", "customer relationship"] },
    { id: "tpl-booking", keywords: ["booking", "appointment", "reservation", "schedule", "calendar", "salon", "spa", "clinic"] },
    { id: "tpl-project", keywords: ["project management", "kanban", "task board", "sprint", "agile", "trello", "asana", "jira"] },
    { id: "tpl-ai", keywords: ["ai platform", "ai saas", "machine learning", "llm", "api platform", "neural", "gpt", "model"] },
    { id: "tpl-social", keywords: ["community", "forum", "discussion", "social", "members", "thread"] },
    { id: "tpl-marketplace", keywords: ["job board", "job portal", "marketplace", "listing", "directory", "hiring", "careers"] },
    { id: "tpl-landing", keywords: ["landing", "saas", "pricing", "hero", "waitlist", "launch"] },
    { id: "tpl-dashboard", keywords: ["dashboard", "analytics", "chart", "kpi", "metric", "report", "admin"] },
    { id: "tpl-portfolio", keywords: ["portfolio", "personal", "resume", "cv", "freelance"] },
    { id: "tpl-blog", keywords: ["blog", "article", "post", "journal", "writing", "content"] },
    { id: "tpl-store", keywords: ["store", "shop", "ecommerce", "e-commerce", "product", "cart", "checkout"] },
  ];
  for (const rule of rules) {
    for (const kw of rule.keywords) {
      if (p.includes(kw)) return rule.id;
    }
  }
  return "base-nextjs";
}
